import React, { useCallback, useEffect, useMemo, useState } from "react";
import Dropdown from "../../common/dropDown";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import {
  fetchCommunicationTemplateById,
  fetchCommunicationTemplates,
  sendBulkEmailSms,
} from "./participantService";

const STEPS = [
  { id: "template", label: "Template", icon: "fas fa-file-alt" },
  { id: "review", label: "Review & Send", icon: "fas fa-paper-plane" },
];

function toTemplateOptions(rows, type) {
  const needle = String(type || "").toLowerCase();
  return (Array.isArray(rows) ? rows : [])
    .filter((r) =>
      String(r.TemplateType || r.Type || "")
        .toLowerCase()
        .includes(needle),
    )
    .map((r) => ({
      label: r.TemplateName || r.Name || `${type} template`,
      value: String(r.TemplateId || r.Id || ""),
    }))
    .filter((o) => o.value);
}

function uniqueByEmail(rows) {
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    const emailKey =
      String(row.EmailId || "")
        .trim()
        .toLowerCase() || `__${row.VolunteerId}`;
    if (seen.has(emailKey)) continue;
    seen.add(emailKey);
    out.push(row);
  }
  return out;
}

/**
 * Inline send column — same Template → Review flow as Feasibility Communicate.
 */
export default function BulkCommunicatePanel({
  recipients = [],
  onSent,
  blockedReason = "",
}) {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const [step, setStep] = useState(0);
  const [channel, setChannel] = useState("email");
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState("");
  const [selectedSmsTemplateId, setSelectedSmsTemplateId] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [smsBody, setSmsBody] = useState("");
  const [overridePrefs, setOverridePrefs] = useState(true);

  const emailEnabled = channel === "email";
  const smsEnabled = channel === "sms";
  const recipientCount = uniqueByEmail(recipients).length;

  const selectedEmailLabel = useMemo(
    () =>
      emailTemplates.find((o) => o.value === selectedEmailTemplateId)?.label ||
      "—",
    [emailTemplates, selectedEmailTemplateId],
  );
  const selectedSmsLabel = useMemo(
    () =>
      smsTemplates.find((o) => o.value === selectedSmsTemplateId)?.label || "—",
    [smsTemplates, selectedSmsTemplateId],
  );

  const resetCompose = useCallback(() => {
    setStep(0);
    setSelectedEmailTemplateId("");
    setSelectedSmsTemplateId("");
    setEmailSubject("");
    setEmailBody("");
    setSmsBody("");
  }, []);

  useEffect(() => {
    if (!userData?.OrganizationId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchCommunicationTemplates(userData).catch(() => []);
        if (cancelled) return;
        setEmailTemplates(toTemplateOptions(rows, "email"));
        setSmsTemplates(toTemplateOptions(rows, "sms"));
      } catch {
        if (!cancelled) {
          setEmailTemplates([]);
          setSmsTemplates([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  // When recipient list changes, drop back to template step.
  useEffect(() => {
    setStep(0);
  }, [recipientCount]);

  const loadTemplateBody = async (templateId, templateType) => {
    if (!templateId) {
      if (templateType === "Email") {
        setEmailSubject("");
        setEmailBody("");
      } else {
        setSmsBody("");
      }
      return;
    }
    showLoader("Loading template...");
    try {
      const row = await fetchCommunicationTemplateById(
        userData,
        templateId,
        templateType,
      );
      if (templateType === "Email") {
        setEmailSubject(row?.EmailSubject || row?.SubjectName || "");
        setEmailBody(row?.TemplateText || row?.EmailTemplateText || "");
      } else {
        setSmsBody(row?.SMSTemplateText || row?.TemplateText || "");
      }
    } catch (err) {
      showAlert({ message: err?.message || "Could not load template." });
    } finally {
      hideLoader();
    }
  };

  const handleChannelChange = (next) => {
    if (next === channel) return;
    setChannel(next);
    setStep(0);
  };

  const nextBlockReason = useMemo(() => {
    if (blockedReason) return blockedReason;
    if (!recipientCount) {
      return "Filter participants before continuing.";
    }
    if (emailEnabled && !selectedEmailTemplateId) {
      return "Select an email template to continue.";
    }
    if (smsEnabled && !selectedSmsTemplateId) {
      return "Select an SMS template to continue.";
    }
    return "";
  }, [
    blockedReason,
    emailEnabled,
    recipientCount,
    selectedEmailTemplateId,
    selectedSmsTemplateId,
    smsEnabled,
  ]);

  const canContinueToReview = () => {
    if (nextBlockReason) {
      showAlert({
        title: "Cannot continue",
        message: nextBlockReason,
        type: "warning",
      });
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (!canContinueToReview()) return;
    setStep(1);
  };

  const handleSend = async () => {
    if (!canContinueToReview()) return;

    const filtersList = uniqueByEmail(recipients).map((row) => ({
      IsEmail: Boolean(row.IsEmail),
      IsSMS: Boolean(row.IsSMS),
      EmailId: row.EmailId || "",
      ContactNo: row.ContactNumber || "",
      VolunteerName: row.VolunteerName || "",
      StudyName: row.StudyNumber || "",
      VisitName: row.VisitName || "",
      AppointmentDateTime: row.AppointmentDate || "",
      VolunteerStatus: row.VolunteerStatus || "",
      AppointmentType: row.AppointmentType || "",
      VolunteerId: row.VolunteerId || "",
    }));

    showLoader("Sending bulk communication...");
    try {
      const response = await sendBulkEmailSms(userData, {
        FiltersList: filtersList,
        EmailBody: emailEnabled ? emailBody || "" : "",
        SMSBody: smsEnabled ? smsBody || "" : "",
        EmailSubject: emailEnabled ? emailSubject || "" : "",
        IsSMS: smsEnabled,
        IsEmail: emailEnabled,
        SMSTemplateId: smsEnabled ? selectedSmsTemplateId || "" : "",
        EmailTemplateId: emailEnabled ? selectedEmailTemplateId || "" : "",
        IsOverRideCommPre: overridePrefs,
      });
      showAlert({
        message:
          response?.message ||
          response?.Message ||
          "Bulk communication has been sent successfully.",
        type: "success",
      });
      resetCompose();
      onSent?.();
    } catch (err) {
      showAlert({
        message: err?.message || "Could not send bulk communication.",
      });
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      <div className="pm-bulk-send-panel pm-feas-comm">
        <div className="pm-bulk-send-panel__title">Send Communication</div>
        <div className="pm-bulk-send-panel__meta">
          {recipientCount
            ? `${recipientCount} recipient(s) ready`
            : "Waiting for filtered participants"}
        </div>

        <nav className="pm-feas-comm__track" aria-label="Communication steps">
          {STEPS.map((item, index) => {
            const isActive = step === index;
            const isComplete = step > index;
            return (
              <React.Fragment key={item.id}>
                <button
                  type="button"
                  className={`pm-feas-comm__node${
                    isActive ? " is-active" : ""
                  }${isComplete ? " is-complete" : ""}`}
                  onClick={() => {
                    if (index === 0) setStep(0);
                    else handleNext();
                  }}
                >
                  <span className="pm-feas-comm__node-circle" aria-hidden="true">
                    {isComplete ? (
                      <i className="fas fa-check" />
                    ) : (
                      <i className={item.icon} />
                    )}
                  </span>
                  <span className="pm-feas-comm__node-label">{item.label}</span>
                </button>
                {index < STEPS.length - 1 ? (
                  <div
                    className={`pm-feas-comm__connector${
                      isComplete ? " is-complete" : ""
                    }${isActive ? " is-current" : ""}`}
                    aria-hidden="true"
                  >
                    <span className="pm-feas-comm__connector-line" />
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
        </nav>

        <div className="pm-bulk-send-panel__body">
          {step === 0 ? (
            <div className="pm-feas-comm__panel">
              <div
                className="pm-feas-comm__segment"
                role="radiogroup"
                aria-label="Channel"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={emailEnabled}
                  className={`pm-feas-comm__segment-btn${
                    emailEnabled ? " is-active" : ""
                  }`}
                  onClick={() => handleChannelChange("email")}
                >
                  <i className="fas fa-envelope" aria-hidden="true" />
                  Email
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={smsEnabled}
                  className={`pm-feas-comm__segment-btn${
                    smsEnabled ? " is-active" : ""
                  }`}
                  onClick={() => handleChannelChange("sms")}
                >
                  <i className="fas fa-sms" aria-hidden="true" />
                  SMS
                </button>
              </div>

              {emailEnabled ? (
                <div className="pm-feas-comm__block">
                  <label className="pm-feas-comm__label">Email template</label>
                  <Dropdown
                    options={emailTemplates}
                    selected={
                      emailTemplates.find(
                        (o) => o.value === selectedEmailTemplateId,
                      ) || null
                    }
                    onSelect={(opt) => {
                      const id = opt?.value || "";
                      setSelectedEmailTemplateId(id);
                      loadTemplateBody(id, "Email");
                    }}
                  />
                  <div className="pm-feas-comm__preview">
                    <div className="pm-feas-comm__preview-head">
                      <i className="fas fa-eye" aria-hidden="true" />
                      Preview
                    </div>
                    <input
                      className="form-control pm-feas-comm__subject"
                      value={emailSubject}
                      readOnly
                      placeholder="Subject"
                    />
                    {emailBody ? (
                      <div
                        className="pm-feas-comm__email-body"
                        dangerouslySetInnerHTML={{ __html: emailBody }}
                      />
                    ) : (
                      <div className="pm-feas-comm__email-body is-empty">
                        Select a template to preview
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="pm-feas-comm__block">
                  <label className="pm-feas-comm__label">SMS template</label>
                  <Dropdown
                    options={smsTemplates}
                    selected={
                      smsTemplates.find(
                        (o) => o.value === selectedSmsTemplateId,
                      ) || null
                    }
                    onSelect={(opt) => {
                      const id = opt?.value || "";
                      setSelectedSmsTemplateId(id);
                      loadTemplateBody(id, "SMS");
                    }}
                  />
                  <div className="pm-feas-comm__preview">
                    <div className="pm-feas-comm__preview-head">
                      <i className="fas fa-eye" aria-hidden="true" />
                      Preview
                    </div>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={smsBody}
                      readOnly
                      placeholder="Select a template to preview the message"
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="pm-feas-comm__panel">
              <div className="pm-feas-comm__review-grid">
                <div className="pm-feas-comm__review-card">
                  <span>{emailEnabled ? "Total emails" : "Total SMS"}</span>
                  <strong>{recipientCount}</strong>
                </div>
                <div className="pm-feas-comm__review-card">
                  <span>
                    {emailEnabled ? "Email template" : "SMS template"}
                  </span>
                  <strong
                    title={emailEnabled ? selectedEmailLabel : selectedSmsLabel}
                  >
                    {emailEnabled ? selectedEmailLabel : selectedSmsLabel}
                  </strong>
                </div>
              </div>

              <label className="pm-feas-comm__switch">
                <input
                  type="checkbox"
                  checked={overridePrefs}
                  onChange={(e) => setOverridePrefs(e.target.checked)}
                />
                <span>Override communication preferences</span>
              </label>
            </div>
          )}
        </div>

        <div className="pm-bulk-send-panel__footer">
          {step === 1 ? (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setStep(0)}
            >
              Back
            </button>
          ) : null}
          {nextBlockReason ? (
            <div className="pm-bulk-send-panel__hint" role="status">
              <i className="fas fa-info-circle" aria-hidden="true" />
              <span>{nextBlockReason}</span>
            </div>
          ) : (
            <span className="pm-bulk-send-panel__hint-spacer" />
          )}
          {step === 0 ? (
            <span
              className="pm-bulk-send-panel__next-hit"
              onClick={() => {
                if (nextBlockReason) canContinueToReview();
              }}
            >
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleNext}
                disabled={Boolean(nextBlockReason)}
                title={nextBlockReason || "Continue to review"}
              >
                Next
                <i className="fas fa-arrow-right ms-1" aria-hidden="true" />
              </button>
            </span>
          ) : (
            <span
              className="pm-bulk-send-panel__next-hit"
              onClick={() => {
                if (nextBlockReason) canContinueToReview();
              }}
            >
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleSend}
                disabled={Boolean(nextBlockReason)}
                title={nextBlockReason || "Send communication"}
              >
                <i className="fas fa-paper-plane me-1" aria-hidden="true" />
                Send
              </button>
            </span>
          )}
        </div>
      </div>
      <InfoAlert {...alertState} onClose={hideAlert} />
    </>
  );
}
